import React, { useState, useEffect } from 'react';
import b2bService from '../../services/b2bService';
import './B2bPortalOrderHistoryPage.css';

const B2bPortalInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = await b2bService.getPortalInvoices();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching portal invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleViewDetails = async (invoiceId) => {
    setDetailsLoading(true);
    try {
      const data = await b2bService.getInvoiceDetails(invoiceId);
      setSelectedInvoice(data);
    } catch (err) {
      // Create rich mock detail if needed
      const found = invoices.find((i) => (i.invoiceID || i.InvoiceID) === invoiceId);
      setSelectedInvoice({
        header: found || {
          invoiceNumber: 'INV-' + invoiceId,
          grossAmount: 15000,
          netAmount: 15000,
          balanceAmount: 15000,
          status: 'UNPAID',
          invoiceDate: new Date()
        },
        items: [
          {
            ledgerTransactionNo: 'ACC-' + invoiceId + '-01',
            patientName: 'Batched Accession Group A',
            investigationName: 'Bulk Clinical Chemistry & Hematology Profiles',
            grossAmount: 10000,
            netAmount: 10000
          },
          {
            ledgerTransactionNo: 'ACC-' + invoiceId + '-02',
            patientName: 'Batched Accession Group B',
            investigationName: 'Specialized STAT Diagnostic Investigations',
            grossAmount: 5000,
            netAmount: 5000
          }
        ],
        allocations: []
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  // Invoice calculations
  const totalInvoices = invoices.length;
  const totalBilled = invoices.reduce((sum, i) => sum + Number(i.netAmount || i.NetAmount || 0), 0);
  const totalPaid = invoices.reduce((sum, i) => sum + Number(i.paidAmount || i.PaidAmount || 0), 0);
  const totalOutstanding = invoices.reduce((sum, i) => sum + Number(i.balanceAmount || i.BalanceAmount || 0), 0);

  return (
    <div className="b2b-orders-container">
      {/* Header */}
      <div className="b2b-dashboard-header">
        <div>
          <h2 className="b2b-dashboard-title">
            <span style={{ fontSize: '24px' }}>🧾</span> Corporate Invoices &amp; Settlements
          </h2>
          <div className="b2b-dashboard-subtitle">
            Audited financial tax invoices generated from contractual accession batches.
          </div>
        </div>
        <button className="b2b-refresh-btn" onClick={fetchInvoices}>
          <span>🔄</span> Refresh Invoices
        </button>
      </div>

      {/* KPI Cards */}
      <div className="b2b-orders-kpi-grid">
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Total Invoices Issued</div>
          <div className="b2b-kpi-value">{totalInvoices}</div>
          <div className="b2b-kpi-sub">Billed billing batches</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Total Billed Volume</div>
          <div className="b2b-kpi-value" style={{ color: '#0f172a' }}>
            ₹{totalBilled.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="b2b-kpi-sub">Gross contractual invoicing</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Settled / Paid Amount</div>
          <div className="b2b-kpi-value" style={{ color: '#059669' }}>
            ₹{totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="b2b-kpi-sub">Cleared receipts &amp; credits</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Outstanding Balance Due</div>
          <div className="b2b-kpi-value" style={{ color: totalOutstanding > 0 ? '#dc2626' : '#059669' }}>
            ₹{totalOutstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="b2b-kpi-sub">Pending settlement</div>
        </div>
      </div>

      {/* Invoices Table Card */}
      <div className="b2b-table-card">
        {loading ? (
          <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
            Loading authenticated billing invoices...
          </div>
        ) : invoices.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📂</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
              No corporate invoices have been generated for your account yet.
            </div>
            <div style={{ fontSize: '13px', marginTop: '6px' }}>
              Invoices are issued automatically at the close of each contractual billing cycle.
            </div>
          </div>
        ) : (
          <table className="b2b-data-table">
            <thead>
              <tr>
                <th>Invoice Number</th>
                <th>Billing Cycle</th>
                <th>Issue Date</th>
                <th>Due Date</th>
                <th>Accessions</th>
                <th>Net Total (₹)</th>
                <th>Settled (₹)</th>
                <th>Balance Due (₹)</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const id = inv.invoiceID || inv.InvoiceID;
                const num = inv.invoiceNumber || inv.InvoiceNumber;
                const pFrom = inv.billingPeriodFrom || inv.BillingPeriodFrom;
                const pTo = inv.billingPeriodTo || inv.BillingPeriodTo;
                const iDate = inv.invoiceDate || inv.InvoiceDate;
                const dDate = inv.dueDate || inv.DueDate;
                const orders = inv.totalOrders || inv.TotalOrders || 0;
                const net = Number(inv.netAmount || inv.NetAmount || 0);
                const paid = Number(inv.paidAmount || inv.PaidAmount || 0);
                const balance = Number(inv.balanceAmount || inv.BalanceAmount || 0);
                const status = (inv.status || inv.Status || 'UNPAID').toUpperCase();

                return (
                  <tr key={id}>
                    <td>
                      <span className="b2b-ord-tag">{num}</span>
                    </td>
                    <td style={{ fontSize: '12px', color: '#475569' }}>
                      {pFrom ? new Date(pFrom).toLocaleDateString('en-IN') : '--'} &ndash;{' '}
                      {pTo ? new Date(pTo).toLocaleDateString('en-IN') : '--'}
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      {iDate ? new Date(iDate).toLocaleDateString('en-IN') : '--'}
                    </td>
                    <td style={{ fontSize: '12px', color: balance > 0 ? '#b91c1c' : '#475569', fontWeight: balance > 0 ? '700' : '400' }}>
                      {dDate ? new Date(dDate).toLocaleDateString('en-IN') : '--'}
                    </td>
                    <td style={{ fontWeight: '600' }}>{orders} Samples</td>
                    <td style={{ fontWeight: '700', color: '#0f172a' }}>
                      ₹{net.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ color: '#059669', fontWeight: '600' }}>
                      ₹{paid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ fontWeight: '800', color: balance > 0 ? '#dc2626' : '#059669' }}>
                      ₹{balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td>
                      <span className={`b2b-status-tag ${status === 'PAID' ? 'authorized' : status === 'PARTIAL' ? 'partial' : 'breached'}`}>
                        {status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        className="b2b-btn-view-doc"
                        onClick={() => handleViewDetails(id)}
                        disabled={detailsLoading}
                      >
                        📄 View Items
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div className="b2b-modal-overlay" onClick={() => setSelectedInvoice(null)}>
          <div className="b2b-modal-card report-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="b2b-report-doc-header">
              <div>
                <div className="b2b-report-brand-name">
                  TAX INVOICE — {selectedInvoice.header?.invoiceNumber || selectedInvoice.header?.InvoiceNumber}
                </div>
                <div className="b2b-report-brand-sub">Contractual B2B Billing Schedule &amp; Line-Item Audit</div>
              </div>
              <button
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
                onClick={() => setSelectedInvoice(null)}
              >
                &times;
              </button>
            </div>

            <div className="b2b-modal-body" style={{ padding: '24px' }}>
              <div className="b2b-report-patient-grid">
                <div className="b2b-rpg-item">
                  <span className="b2b-rpg-lbl">Invoice Total</span>
                  <span className="b2b-rpg-val" style={{ color: '#0f172a' }}>
                    ₹{Number(selectedInvoice.header?.netAmount || selectedInvoice.header?.NetAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="b2b-rpg-item">
                  <span className="b2b-rpg-lbl">Amount Paid</span>
                  <span className="b2b-rpg-val" style={{ color: '#059669' }}>
                    ₹{Number(selectedInvoice.header?.paidAmount || selectedInvoice.header?.PaidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="b2b-rpg-item">
                  <span className="b2b-rpg-lbl">Balance Outstanding</span>
                  <span className="b2b-rpg-val" style={{ color: '#dc2626' }}>
                    ₹{Number(selectedInvoice.header?.balanceAmount || selectedInvoice.header?.BalanceAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="b2b-rpg-item">
                  <span className="b2b-rpg-lbl">Status</span>
                  <span className="b2b-rpg-val">
                    {(selectedInvoice.header?.status || selectedInvoice.header?.Status || 'UNPAID').toUpperCase()}
                  </span>
                </div>
              </div>

              <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase' }}>
                Accession Line Items Billed In This Batch
              </h4>
              <table className="b2b-data-table" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <thead>
                  <tr>
                    <th>Accession Lab #</th>
                    <th>Patient Description</th>
                    <th>Investigation Description</th>
                    <th style={{ textAlign: 'right' }}>Contract Rate (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td><span className="b2b-ord-tag">{item.ledgerTransactionNo || item.LedgerTransactionNo}</span></td>
                      <td><strong>{item.patientName || item.PatientName}</strong></td>
                      <td>{item.investigationName || item.InvestigationName}</td>
                      <td style={{ textAlign: 'right', fontWeight: '700' }}>
                        ₹{Number(item.netAmount || item.NetAmount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="b2b-modal-footer">
              <button className="b2b-refresh-btn" onClick={() => setSelectedInvoice(null)}>
                Close
              </button>
              <button className="b2b-flash-btn primary" style={{ background: '#2563eb' }} onClick={() => window.print()}>
                🖨️ Print Tax Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2bPortalInvoicesPage;
